import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaAward, FaUsers, FaServer, FaHeadset, 
  FaRocket, FaHandshake, FaGlobeAfrica, FaLightbulb,
  FaArrowRight, FaBuilding, FaCode, FaHeart, FaCalendar,
  FaCloud, FaMobile, FaChartLine
} from 'react-icons/fa';

const values = [
  { icon: FaAward, title: 'Excellence', description: 'We deliver high-quality solutions that exceed expectations.' },
  { icon: FaUsers, title: 'Collaboration', description: 'We work closely with clients to understand and meet their needs.' },
  { icon: FaServer, title: 'Innovation', description: 'We embrace cutting-edge technology to drive digital transformation.' },
  { icon: FaHeadset, title: 'Support', description: 'We provide 24/7 support to ensure business continuity.' },
];

const milestones = [
  { year: '2010', title: 'Micropoint Begins', description: 'Started Operations providing software solutions to organizations in Kenya.' },
  { year: '2012', title: 'PowerFinancials', description: 'PowerFinancials enters the SACCO and microfinace market.' },
  { year: '2013', title: 'Company Incorporation', description: 'Micropoint Systems Limited formally Incorporated.' },
  { year: '2018', title: 'Regional Expansion', description: 'Expanded solutions and customer base across East Africa.' },
  { year: '2020', title: 'Digital Innovation', description: 'Expanded mobile, digital lending and payment integration capabilities.' },
  { year: '2024', title: 'Growth & Impact', description: 'Serving over 150+ organizations across multiple industries.' },
];

export default function About() {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              About <span className="text-blue-600">Micropoint Systems</span>
            </h1>
          </motion.div>
        </div>

        {/* ─── Who We Are ─── */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Who We Are</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-3 text-sm">
              From a focused software development company to a regional enterprise technology partner, 
              Micropoint Systems has grown by solving real business problems for organizations across Africa.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3 text-sm">
              Our journey began with a simple focus: build practical software that helps organizations 
              work better. That philosophy led to the development of PowerFinancials and our expansion 
              into enterprise, healthcare, education and other specialized solutions.
            </p>
            <p className="text-slate-600 leading-relaxed text-sm">
              Today, we combine deep industry experience with modern cloud, mobile and enterprise 
              technologies to help organizations improve efficiency, strengthen control and deliver 
              better services to their customers.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { number: '15+', label: 'Years Experience', icon: FaAward },
              { number: '150+', label: 'Happy Clients', icon: FaUsers },
              { number: '50+', label: 'Implementations', icon: FaServer },
              { number: '24/7', label: 'Technical Support', icon: FaHeadset },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-5 text-center border border-slate-200 hover:border-blue-300 transition-all hover:shadow-md">
                  <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                    <Icon className="text-xl text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{stat.number}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* ─── Our Journey - Timeline ─── */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <FaCalendar className="text-blue-600 text-2xl" />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                Our <span className="text-blue-600">History</span>
              </h2>
            </div>
            <p className="text-slate-500 text-sm max-w-xl mx-auto">
              Key milestones that have shaped our journey
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-blue-200 hidden md:block" />
            
            <div className="space-y-6 md:space-y-0">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className={`flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } gap-3 md:gap-6`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md">
                      <span className="text-blue-600 font-bold text-lg">{milestone.year}</span>
                      <h4 className="font-bold text-slate-900 text-sm mt-0.5">{milestone.title}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-blue-600 border-4 border-white shadow-md" />
                  </div>
                  
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Our Values ─── */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Our <span className="text-blue-600">Values</span>
            </h2>
            <p className="text-slate-500 text-sm max-w-xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-white rounded-xl p-5 text-center border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
                >
                  <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-all">
                    <Icon className="text-2xl text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-1">{value.title}</h3>
                  <p className="text-xs text-slate-500">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ─── Why Choose Us ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative overflow-hidden rounded-xl p-6 md:p-8 text-white mb-16"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Why Choose <span className="text-blue-400">Micropoint?</span>
              </h2>
              <p className="text-white/60 text-sm md:text-base">
                From a focused software development company to a regional enterprise technology partner, 
                we have grown by solving real business problems for organizations across Africa.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: FaRocket, label: 'Innovation First' },
                { icon: FaHandshake, label: 'Trusted Partner' },
                { icon: FaGlobeAfrica, label: 'Regional Expertise' },
                { icon: FaLightbulb, label: 'Expert Team' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center gap-2 bg-white/10 rounded-lg p-2.5 hover:bg-white/20 transition-all">
                    <Icon className="text-blue-400 text-base" />
                    <span className="text-xs md:text-sm font-medium text-white">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ─── CTA ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 text-center shadow-sm hover:shadow-md transition-all"
        >
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Ready to Transform <span className="text-blue-600">Your Business?</span>
          </h3>
          <p className="text-slate-500 text-sm mb-5 max-w-lg mx-auto">
            Join 150+ organizations that trust Micropoint Systems for their digital transformation journey.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-2.5 rounded-full font-semibold transition-all inline-flex items-center gap-2 hover:scale-105 hover:shadow-lg text-sm"
            >
              Get in Touch <FaArrowRight className="text-xs" />
            </Link>
            <Link
              to="/careers"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 md:px-8 py-2.5 rounded-full font-semibold transition-all inline-flex items-center gap-2 hover:scale-105 text-sm"
            >
              Join Our Team
            </Link>
          </div>
        </motion.div>

      </div>
    </section>
  );
}